<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { groupsFor, homeHrefFor, inboxBadges, type Role } from '../../navigation'
import { useOwnerInboxStore } from '../../stores/ownerInbox'
import { signOut } from '../../signOut'
import { isActive, isAnyActive } from './useActiveNav'
import { reservedWidth, visibleCount } from './fitNav'
import NotificationBell from '../NotificationBell.vue'
import ThemeToggle from '../ThemeToggle.vue'
import NavIcon from './NavIcon.vue'
import NavBadge from './NavBadge.vue'

const page = usePage()
const inboxStore = useOwnerInboxStore()
const user = computed(() => page.props.current_user as { role: Role; fio: string })
const coworking = computed(() => page.props.coworking_available !== false)
const doorOpen = computed(() => page.props.door_access_open !== false)
// Бейджи очередей владельца (owner_inbox): у клиента и админа пропа нет — набор пуст.
const badges = computed(() => inboxBadges(inboxStore.counts))
const groups = computed(() => groupsFor(user.value.role, coworking.value, doorOpen.value))
// «Профиль» на десктопе живёт в выпадашке под аватаром, а не ссылкой в шапке:
// личных пунктов у клиента уже шесть, и учётная страница среди них — самая редкая.
// Группу в navigation.ts при этом не меняем: там пункт нужен группе personal,
// иначе он пропадёт из мобильного таб-бара и уедет homeHrefFor.
const MENU_KEYS = ['profile']
const headerPersonal = computed(() => groups.value.personal.filter((i) => !MENU_KEYS.includes(i.key)))
const menuItems = computed(() => groups.value.personal.filter((i) => MENU_KEYS.includes(i.key)))
const menuActive = computed(() => isAnyActive(menuItems.value))
// Логотип не может вести в закрытый раздел: у иногороднего /schedule отдаёт редирект.
const homeHref = computed(() => homeHrefFor(user.value.role, coworking.value, doorOpen.value))
const initials = computed(() => user.value.fio.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase())
// Динамическая привязка вместо статичного src: Vue иначе транслирует
// абсолютный путь в статический import ассета при production-сборке
// (public/header-logo.png не входит в корень Vite — app/frontend — и
// ломает `vite build` с UNRESOLVED_IMPORT).
const logoSrc = '/header-logo.png'

const mgmtOpen = ref(false)
const menuOpen = ref(false)
const moreOpen = ref(false)
const anyMenuOpen = computed(() => mgmtOpen.value || menuOpen.value || moreOpen.value)
const mgmtActive = computed(() => isAnyActive(groups.value.management))
function closeMenus() { mgmtOpen.value = false; menuOpen.value = false; moreOpen.value = false }

// Переполнение шапки. Личных пунктов у владельца восемь плюс «Управление ▾» —
// это 830px одних только ссылок, и в ширину до ~1250px они не влезали: строка
// не переносилась и не сжималась, а наезжала на колокольчик с аватаром.
// Порогом (md/lg/xl) это не лечится — нужная ширина зависит от роли и от длины
// ФИО в правом углу, поэтому считаем по факту: меряем ссылки и прячем хвост в
// выпадашку «Ещё ▾». «Управление» не вытесняем: выпадашка внутри выпадашки.
const GAP = 4 // gap-1 между пунктами nav
const navRef = ref<HTMLElement | null>(null)
const probeRef = ref<HTMLElement | null>(null)
const availableWidth = ref(0)
const itemWidths = ref<number[]>([])
const moreWidth = ref(0)

// Ширины берём с невидимого ряда-двойника (probe), а не с самих ссылок: у
// спрятанного пункта ширины нет, и вернуть его на экран при расширении окна было
// бы уже не по чему. Probe лежит absolute и вне <nav>, поэтому не занимает места
// и не попадает в текст навигации.
function measure(): void {
  const nav = navRef.value
  const probe = probeRef.value
  if (!nav || !probe) return

  // Дробные ширины, а не offsetWidth: тот округляет каждый пункт, и на восьми
  // пунктах ошибка набегает до пикселя — последний пункт срезался на долю px.
  const width = (el: Element | null) => el?.getBoundingClientRect().width ?? 0
  const probeItems = [...probe.querySelectorAll<HTMLElement>('[data-probe-item]')]
  itemWidths.value = probeItems.map(width)
  moreWidth.value = width(probe.querySelector('[data-probe-more]'))

  // Обязательная часть строки — «Управление ▾» и владельческие ссылки: они
  // видимы всегда, значит их ширина уменьшает бюджет личных пунктов.
  const fixed = [...nav.querySelectorAll<HTMLElement>('[data-nav-fixed]')].map(width)
  availableWidth.value = width(nav) - reservedWidth(fixed, GAP)
}

const shownCount = computed(() =>
  visibleCount(availableWidth.value, itemWidths.value, GAP, moreWidth.value),
)
const visibleItems = computed(() => headerPersonal.value.slice(0, shownCount.value))
const overflowItems = computed(() => headerPersonal.value.slice(shownCount.value))
const overflowActive = computed(() => isAnyActive(overflowItems.value))

let observer: ResizeObserver | null = null

onMounted(() => {
  void measure()
  // Ширина ссылок меняется вместе со шрифтом: до его загрузки замер даёт
  // системный шрифт и пунктов «влезает» больше, чем влезет на самом деле.
  document.fonts?.ready.then(measure).catch(() => {})
  // ResizeObserver в jsdom нет — там замера и не будет, а нулевые ширины
  // означают «показать всё» (см. fitNav.ts).
  if (typeof ResizeObserver === 'undefined' || !navRef.value) return
  observer = new ResizeObserver(() => measure())
  observer.observe(navRef.value)
})

onUnmounted(() => observer?.disconnect())

// Набор пунктов меняется на лету: coworking_available и door_access_open приходят
// пропсами страницы и на разных экранах разные. Старые ширины после такой смены
// относятся к другому списку — перемеряем, дождавшись перерисовки probe.
watch(headerPersonal, async () => {
  moreOpen.value = false
  await nextTick()
  measure()
})

const linkClass = (href: string) =>
  isActive(href)
    ? 'rounded-md bg-accent-soft px-3 py-2 text-sm font-semibold text-accent-ink'
    : 'rounded-md px-3 py-2 text-sm font-medium text-muted hover:bg-surface-2'

</script>

<template>
  <header class="relative z-50 flex h-14 items-center justify-between gap-3 border-b border-border bg-surface px-5">
    <!-- Backdrop: клик вне выпадашки закрывает её. Он ниже самих выпадашек (z-10 < z-20),
         поэтому ссылки в меню кликаются, а клик мимо — закрывает. Это заменяет прежний
         @blur на кнопке, который срабатывал раньше клика по ссылке и ломал переходы. -->
    <div v-if="anyMenuOpen" data-testid="menu-backdrop" class="fixed inset-0 z-10" @click="closeMenus" />

    <div class="flex min-w-0 flex-1 items-center gap-6">
      <Link :href="homeHref" class="flex shrink-0 items-center" aria-label="Наша Душа">
        <img :src="logoSrc" alt="Наша Душа" class="h-6 w-auto" />
      </Link>

      <!-- Ряд-двойник для замеров: невидим, вне потока, вне <nav> (иначе его
           метки попали бы в текст навигации и в её тесты). Держит ширины всех
           личных пунктов, включая спрятанные, — по ним пункты возвращаются в
           шапку при расширении окна. -->
      <div ref="probeRef" aria-hidden="true"
           class="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-1">
        <span v-for="item in headerPersonal" :key="item.key" data-probe-item
              :class="[linkClass(item.href), 'inline-flex items-center']">
          {{ item.label }}<NavBadge v-if="badges[item.key]" :label="badges[item.key]" class="ml-1.5" />
        </span>
        <span data-probe-more class="rounded-md px-3 py-2 text-sm font-medium">Ещё ▾</span>
      </div>

      <!-- Клипует (и сжимается) только внутренний ряд личных пунктов, а не сам
           <nav>: панель выпадашки висит absolute НИЖЕ границы своего контейнера,
           и внутри клипающего элемента её срезало бы целиком — в браузере это
           выглядело как «меню не открывается по клику». Поэтому обе выпадашки
           лежат рядом с клипающим рядом, а не в нём, но по-прежнему внутри
           landmark-а <nav>: для скринридера это одна навигация.
           <nav> при этом тянется во всю строку — на него смотрит ResizeObserver,
           и ширина ряда личных пунктов за ним не годится: она равна своему
           содержимому и при расширении окна не меняется. -->
      <nav ref="navRef" class="flex min-w-0 flex-1 items-center gap-1">
        <div data-nav-clip class="flex min-w-0 items-center gap-1 overflow-hidden">
          <component :is="item.external ? 'a' : Link" v-for="item in visibleItems" :key="item.key" :href="item.href"
                :class="[linkClass(item.href), 'inline-flex shrink-0 items-center whitespace-nowrap']"
                :aria-current="isActive(item.href) ? 'page' : undefined">
            {{ item.label }}
            <NavBadge v-if="badges[item.key]" :label="badges[item.key]" class="ml-1.5" />
          </component>
        </div>

        <div class="flex shrink-0 items-center gap-1">
          <div v-if="overflowItems.length" class="relative shrink-0">
            <button type="button" data-testid="more-toggle"
                    class="relative z-20 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors"
                    :class="overflowActive
                      ? 'bg-accent-soft font-semibold text-accent-ink'
                      : 'font-medium text-muted hover:bg-surface-2'"
                    aria-haspopup="menu"
                    :aria-expanded="moreOpen"
                    @click="moreOpen = !moreOpen">
              Ещё ▾
            </button>
            <div v-if="moreOpen" class="absolute left-0 top-full z-20 mt-1 min-w-52 rounded-lg border border-border bg-surface p-1 shadow-lg">
              <component :is="item.external ? 'a' : Link" v-for="item in overflowItems" :key="item.key"
                    :href="item.href" @click="closeMenus"
                    :data-testid="`more-${item.key}`"
                    :aria-current="isActive(item.href) ? 'page' : undefined"
                    class="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-surface-2"
                    :class="isActive(item.href) ? 'bg-accent-soft font-semibold text-accent-ink' : 'text-text'">
                <NavIcon :name="item.icon" class="h-4 w-4 text-muted" /> {{ item.label }}
                <NavBadge v-if="badges[item.key]" :label="badges[item.key]" class="ml-auto" />
              </component>
            </div>
          </div>

          <div v-if="groups.management.length" data-nav-fixed class="relative shrink-0">
            <button type="button" data-testid="mgmt-toggle"
                    class="relative z-20 rounded-md px-3 py-2 text-sm transition-colors"
                    :class="mgmtActive
                      ? 'bg-accent-soft font-semibold text-accent-ink'
                      : 'font-medium text-muted hover:bg-surface-2'"
                    @click="mgmtOpen = !mgmtOpen">
              Управление ▾
            </button>
            <div v-if="mgmtOpen" data-testid="mgmt-menu" class="absolute left-0 top-full z-20 mt-1 min-w-52 rounded-lg border border-border bg-surface p-1 shadow-lg">
              <Link v-for="item in groups.management" :key="item.key" :href="item.href" @click="closeMenus"
                    :data-testid="`mgmt-item-${item.key}`"
                    :aria-current="isActive(item.href) ? 'page' : undefined"
                    class="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-surface-2"
                    :class="isActive(item.href) ? 'bg-accent-soft font-semibold text-accent-ink' : 'text-text'">
                <NavIcon :name="item.icon" class="h-4 w-4 text-muted" /> {{ item.label }}
                <NavBadge v-if="badges[item.key]" :label="badges[item.key]" class="ml-auto" />
              </Link>
            </div>
          </div>

          <Link v-for="item in groups.owner" :key="item.key" :href="item.href" data-nav-fixed
                :class="[linkClass(item.href), 'shrink-0 whitespace-nowrap']"
                :aria-current="isActive(item.href) ? 'page' : undefined">
            {{ item.label }}
          </Link>
        </div>
      </nav>
    </div>

    <div class="flex shrink-0 items-center gap-4">
      <ThemeToggle />
      <NotificationBell />
      <div class="relative">
        <button type="button" data-testid="user-menu"
                class="relative z-20 flex min-w-0 items-center gap-2 rounded-md px-1 py-1"
                :class="menuActive ? 'bg-accent-soft' : ''"
                :aria-current="menuActive ? 'page' : undefined"
                aria-haspopup="menu"
                :aria-expanded="menuOpen"
                @click="menuOpen = !menuOpen">
          <span class="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent text-xs font-bold text-white">{{ initials }}</span>
          <span class="truncate max-w-[12rem] text-sm font-semibold text-text">{{ user.fio }}</span>
        </button>
        <div v-if="menuOpen" class="absolute right-0 top-full z-20 mt-1 min-w-40 rounded-lg border border-border bg-surface p-1 shadow-lg">
          <Link v-for="item in menuItems" :key="item.key" :href="item.href" @click="closeMenus"
                :data-testid="`menu-${item.key}`"
                :aria-current="isActive(item.href) ? 'page' : undefined"
                class="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-surface-2"
                :class="isActive(item.href) ? 'bg-accent-soft font-semibold text-accent-ink' : 'text-text'">
            <NavIcon :name="item.icon" class="h-4 w-4 text-muted" /> {{ item.label }}
          </Link>
          <button type="button" data-testid="signout" @click="signOut"
                  class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-crit hover:bg-surface-2">
            <NavIcon name="logout" class="h-4 w-4" /> Выйти
          </button>
        </div>
      </div>
    </div>
  </header>
</template>
